import { keyframes } from "@stitches/react";
import { cloneElement, useCallback, useEffect, useState } from "react";
import useMobile from "~/presentation/shared/hooks/useMobile";
import { sidebarService, SidebarType } from "~/services/SidebarService";
import { ObserverEvent } from "~/services/SimpleObservable";
import { Flex } from "../Flex";
import { Dialog } from "../Dialog";
import { SidebarContent } from "./SidebarContent";
import { SidebarOverlay } from "./SidebarOverlay";

const slideInRight = keyframes({
  from: { transform: "translateX(100%)" },
  to: { transform: "translateX(0)" },
});

const slideOutRight = keyframes({
  from: { transform: "translateX(0)" },
  to: { transform: "translateX(100%)" },
});

const slideInLeft = keyframes({
  from: { transform: "translateX(-100%)" },
  to: { transform: "translateX(0)" },
});

const slideOutLeft = keyframes({
  from: { transform: "translateX(0)" },
  to: { transform: "translateX(-100%)" },
});

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

export function SidebarContainer() {
  const [sidebarPack, setSidebarPack] = useState<SidebarType[]>([]);
  const [exitingSidebars, setExitingSidebars] = useState<number[]>([]);
  const isMobile = useMobile();

  const hideSidebarById = useCallback(
    (id: number, fromService = false) => {
      setExitingSidebars((prev) => [...prev, id]);

      // Só notifica o service se não veio do próprio service
      if (!fromService) {
        sidebarService.closeSidebar(id);
      }

      setTimeout(() => {
        setSidebarPack((old) => old.filter((d) => d.id !== id));
        setExitingSidebars((prev) => prev.filter((sid) => sid !== id));
      }, 300); // Duração da animação
    },
    [setSidebarPack],
  );

  const onUpdate = (change?: ObserverEvent): void => {
    if (change?.type === "hide") {
      hideSidebarById(change.data, true); // fromService = true
      return;
    }

    const sidebar = sidebarService.getSidebar();

    setSidebarPack((prev) => {
      const alreadyExists = prev.some((s) => s.id === sidebar.id);

      if (alreadyExists) return prev;

      return [...prev, { ...sidebar }];
    });
  };

  useEffect(() => {
    sidebarService.subscribe(onUpdate);
  }, []);

  useEffect(() => {
    if (sidebarPack.length > 0) {
      document.body.style.overflow = "hidden";
      return;
    }

    document.body.style.overflow = "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarPack.length]);

  return (
    <Flex css={{ width: "100%" }}>
      {sidebarPack.map((sidebar, index) => (
        <Dialog key={`${sidebar.id}-${index}`} open css={{ width: "fit-content" }}>
          <SidebarOverlay
            css={{
              animation: exitingSidebars.includes(sidebar.id)
                ? `${fadeOut} 300ms ease-out forwards`
                : `${fadeIn} 300ms ease-in forwards`,
            }}
            onClick={() => {
              if (!sidebar.config?.noClose) {
                hideSidebarById(sidebar.id);
              }
            }}
          />

          <SidebarContent
            css={{
              borderTopLeftRadius:
                (sidebar.config?.position || "right") === "right" ? "$rounded" : 0,
              borderBottomLeftRadius:
                (sidebar.config?.position || "right") === "right" ? "$rounded" : 0,
              borderTopRightRadius:
                (sidebar.config?.position || "right") === "left" ? "$rounded" : 0,
              borderBottomRightRadius:
                (sidebar.config?.position || "right") === "left" ? "$rounded" : 0,
              [sidebar.config?.position || "right"]: 0,
              width: sidebar.config?.width || (isMobile ? "100vw" : "400px"),
              boxShadow:
                (sidebar.config?.position || "right") === "left"
                  ? "4px 0 15px rgba(0, 0, 0, 0.1)"
                  : "-4px 0 15px rgba(0, 0, 0, 0.1)",
              animation: exitingSidebars.includes(sidebar.id)
                ? (sidebar.config?.position || "right") === "left"
                  ? `${slideOutLeft} 300ms ease-out forwards`
                  : `${slideOutRight} 300ms ease-out forwards`
                : (sidebar.config?.position || "right") === "left"
                  ? `${slideInLeft} 300ms ease-in forwards`
                  : `${slideInRight} 300ms ease-in forwards`,
            }}
          >
            {cloneElement(sidebar.component, {
              onCloseClick: () => hideSidebarById(sidebar.id),
            })}
          </SidebarContent>
        </Dialog>
      ))}
    </Flex>
  );
}
